import { useEffect } from 'react';
import { type Session } from 'talkjs/all';
import {
  trackLocationRequestSentFromMessage,
  type LocationRequestPathway,
} from '../../../gainsightTrackings/locationRequestTrackings';

interface UseTrackSentLocationRequestsArgs {
  chatSession: Session | null;
  /** The text pathway this chat sends over. */
  pathwayType: LocationRequestPathway;
  callerID: string | null | undefined;
  conversationId: string | undefined;
}

/**
 * Reports a location request as sent once it appears on the chat session.
 *
 * Owns its own onMessage subscription rather than riding along on a chat's
 * existing handler, so the unsubscribe sits next to the subscribe. That matters
 * here more than usual: createTalkSession returns a process-wide singleton, so a
 * handler left behind by a re-render keeps reporting for conversations the
 * dispatcher has since moved on from.
 */
export const useTrackSentLocationRequests = ({
  chatSession,
  pathwayType,
  callerID,
  conversationId,
}: UseTrackSentLocationRequestsArgs): void => {
  useEffect(() => {
    if (!chatSession || !conversationId) return;

    const subscription = chatSession.onMessage(message =>
      trackLocationRequestSentFromMessage(message, {
        pathwayType,
        callerID,
        conversationId,
      }),
    );

    return () => subscription.unsubscribe();
  }, [chatSession, conversationId, callerID, pathwayType]);
};
